import * as Cl from '@design-system/component-library';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { dsClass } from '../../common/constants/dsClasses.js';
import { CommonErrorType, RelatedPartyRoles } from '../../common/enums.js';
import {
  acceptQuoteMsg,
  createdDateMsg,
  customerMsg,
  includedItemsMsg,
  quoteIdMsg,
  quoteMsg,
  quoteUpdateFailedMsg,
  quoteValidityMsg,
  rejectQuoteMsg,
  salesContactMsg,
  statusMsg,
  successAcceptQuoteMsg,
  successRejectQuoteMsg,
  summaryMsg,
  t,
} from '../../common/i18n/index.js';
import { updateProposalRequestById } from '../../common/loaders.js';
import { getLocalizedDateString } from '../../common/utils/dateUtils.js';
import { QuoteStatusType } from '../../generated/api/quoteStatusType.js';
import { UpdateProposalRequest } from '../../generated/api/updateProposalRequest.js';
import { setFooterVisibility, setSmallHeaderVisibility } from '../../selfservice/actions/index.js';
import { DetailsWrapper } from '../DetailsWrapper/index.js';
import { ConfirmationModal } from '../Modal/ConfirmationModal.js';
import { Status } from '../Status/Status.js';
import { SystemError } from '../SystemError/SystemError.js';
import { AcceptQuoteModalContentCoCo } from './AcceptQuoteModalContentCoCo.js';
import { QuoteFooterCoCo } from './QuoteFooterCoCo.js';
import { QuoteProductOrderItemsCoCo } from './QuoteProductOrderItemsCoCo.js';
import { getQuoteStatus } from './QuotesCommonUtilsCoCo.js';
import { RejectQuoteModalContentCoCo } from './RejectQuoteModalContentCoCo.js';
import ActionEnum = UpdateProposalRequest.ActionEnum;
import type { QuoteStatus } from './QuotesCommonUtilsCoCo.js';
import type { CommonError } from '../../common/types/errors.js';
import type { ProposalResponse } from '../../generated/api/proposalResponse.js';
import type { RelatedParty } from '../../generated/api/relatedParty.js';
import type { ChangeEvent, JSX } from 'react';

import './QuoteDetailsCoCo.scss';

interface QuoteDetailsProps {
  breadCrumbs?: JSX.Element;
  proposal: ProposalResponse;
}

const getQuoteSummary = (summary: string) => {
  return (
    <div className={dsClass.PADDING_BOTTOM_4}>
      <Cl.Description
        items={[
          {
            title: t.QF5T(summaryMsg),
            description: <span className="of-quote-summary-one-line">{summary}</span>,
          },
        ]}
      />
    </div>
  );
};

const getQuoteStatusField = (quoteStatus: QuoteStatus) => (
  <Status color={quoteStatus.color} textAfterBadge={quoteStatus.text} />
);

const getRelatedPartyNameByRole = (relatedParty?: RelatedParty[], role?: string) => {
  return relatedParty?.find(party => party.role === role)?.name;
};

const getQuoteDetails = (proposal: ProposalResponse, status: QuoteStatusType) => {
  return (
    <div>
      <Cl.Description
        items={[
          {
            title: t.ASQT(statusMsg),
            description: getQuoteStatusField(getQuoteStatus(status)),
          },
          {
            title: t.R9YZ(quoteIdMsg),
            description: proposal.id,
          },
          {
            title: t.P11A(createdDateMsg),
            description: `${getLocalizedDateString(new Date(proposal.creationDate))}`,
          },
          {
            title: t.IY5V(customerMsg),
            description: getRelatedPartyNameByRole(proposal.relatedParty, RelatedPartyRoles.CUSTOMER),
          },
          {
            title: t.DU8X(quoteValidityMsg),
            description: `${getLocalizedDateString(new Date(proposal.expirationDate))}`,
          },
          {
            title: t.N86G(salesContactMsg),
            description: getRelatedPartyNameByRole(proposal.relatedParty, RelatedPartyRoles.PROPOSAL_CREATOR),
          },
        ]}
      />
    </div>
  );
};

export const QuoteDetailsCoCo = ({ breadCrumbs, proposal }: QuoteDetailsProps) => {
  const dispatch = useDispatch();
  const proposalId = proposal ? proposal.id : '';
  const [quoteStatus, setQuoteStatus] = useState<QuoteStatusType>(proposal.status);
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState<boolean>(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState<boolean>(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showRejectReasonError, setShowRejectReasonError] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isTermsAccepted, setIsTermsAccepted] = useState<boolean>(false);
  const [showTermsRequiredError, setShowTermsRequiredError] = useState<boolean>(false);
  const [errors, setErrors] = useState<CommonError[] | null>(null);

  useEffect(() => {
    dispatch(setFooterVisibility(false));
    dispatch(setSmallHeaderVisibility(false));
    return () => {
      dispatch(setFooterVisibility(true));
      dispatch(setSmallHeaderVisibility(true));
    };
  }, [dispatch]);

  const onAccept = () => {
    setIsAcceptModalOpen(true);
  };

  const onReject = () => {
    setIsRejectModalOpen(true);
  };

  const onRejectionReasonChange = (reason: string) => {
    setRejectionReason(reason);
    if (reason.trim()) {
      setShowRejectReasonError(false);
    }
  };

  const onAcceptance = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setIsTermsAccepted(checked);

    if (checked) {
      setShowTermsRequiredError(false);
    }
  };

  const updateProposal = async (payload: UpdateProposalRequest) => {
    try {
      await updateProposalRequestById(proposalId, payload);

      if (payload.action === ActionEnum.accept) {
        setQuoteStatus(QuoteStatusType.ACCEPTED);
        setIsAcceptModalOpen(false);
        setSuccessMessage(t.GAGZ(successAcceptQuoteMsg, proposal.id, proposal.name));
      } else {
        setQuoteStatus(QuoteStatusType.REJECTED);
        setIsRejectModalOpen(false);
        setSuccessMessage(t.GAGZ(successRejectQuoteMsg, proposal.id, proposal.name));
      }

      setShowSuccessNotification(true);
      setShowRejectReasonError(false);
    } catch {
      setIsAcceptModalOpen(false);
      setIsRejectModalOpen(false);
      setShowSuccessNotification(false);
      setErrors([{ type: CommonErrorType.SYSTEM, message: t.EPDD(quoteUpdateFailedMsg) }]);
    }
  };

  const onAcceptAction = () => {
    if (!isTermsAccepted) {
      setShowTermsRequiredError(true);
      return;
    }
    setShowTermsRequiredError(false);
    const payload: UpdateProposalRequest = {
      action: UpdateProposalRequest.ActionEnum.accept,
      comment: 'We agree to the terms and conditions of this proposal.',
    };
    updateProposal(payload);
  };

  const onRejectAction = () => {
    if (!rejectionReason.trim()) {
      setShowRejectReasonError(true);
      return;
    }
    const payload: UpdateProposalRequest = {
      action: UpdateProposalRequest.ActionEnum.reject,
      comment: rejectionReason.trim(),
    };
    updateProposal(payload);
  };

  if (errors) {
    return <SystemError errors={errors} />;
  }
  return (
    <div className="of-quote-details">
      {showSuccessNotification && (
        <div className="of-quote-top-notification">
          <Cl.Notification
            type={quoteStatus === QuoteStatusType.ACCEPTED ? 'success' : 'default'}
            text={successMessage}
            onNotificationHide={() => setShowSuccessNotification(false)}
          />
        </div>
      )}
      <DetailsWrapper
        detailsTop={breadCrumbs}
        headingTop={t.MNYU(quoteMsg)}
        heading={proposal.name}
        heroPicto="quote"
        id={`proposal-details-${proposalId}`}
      >
        {!!proposal.description && <div>{getQuoteSummary(proposal.description)}</div>}
        <div>{getQuoteDetails(proposal, quoteStatus)}</div>
        <div className="section-separator"></div>

        {proposal.proposalOrder?.productOrderItems && (
          <>
            <span className="of-quote-included-items-text">{t.TZWB(includedItemsMsg)}</span>
            <QuoteProductOrderItemsCoCo productOrderItems={proposal.proposalOrder.productOrderItems} />
          </>
        )}
      </DetailsWrapper>
      <QuoteFooterCoCo
        totalOneTimePrice={proposal.proposalOrder?.priceSummary?.oneTimePrice}
        totalRecurringPrice={proposal?.proposalOrder?.priceSummary?.recurringPrice}
        onAccept={onAccept}
        onReject={onReject}
        quoteStatus={quoteStatus}
      ></QuoteFooterCoCo>
      {(isAcceptModalOpen || isRejectModalOpen) && (
        <ConfirmationModal
          size="l"
          onCancel={isAcceptModalOpen ? setIsAcceptModalOpen.bind(null, false) : setIsRejectModalOpen.bind(null, false)}
          onConfirm={isAcceptModalOpen ? onAcceptAction : onRejectAction}
          heading={isAcceptModalOpen ? t.WYZ2(acceptQuoteMsg) : t.SOC0(rejectQuoteMsg)}
          confirmText={isAcceptModalOpen ? t.P5RQ('Accept') : t.ONSX('Reject')}
        >
          {isAcceptModalOpen ? (
            <AcceptQuoteModalContentCoCo
              onAcceptance={onAcceptance}
              isTermsAccepted={isTermsAccepted}
              showTermsRequiredError={showTermsRequiredError}
            />
          ) : (
            <RejectQuoteModalContentCoCo
              onReasonChange={onRejectionReasonChange}
              showReasonRequiredError={showRejectReasonError}
            />
          )}
        </ConfirmationModal>
      )}
    </div>
  );
};
